import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPurchase, updatePurchase } from './PurchaseService';
import './PurchasingAdminPanel.css';

const PurchaseReceiptDetails = ({ onCancel, onSubmit, initialData }) => {
  const [billFile, setBillFile] = useState(null);
  
  // Responsive styles
  const responsiveStyles = {
    formSection: {
      padding: '10px 0',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: 'clamp(0.9rem, 3vw, 1rem)',
      fontWeight: 'bold',
      marginBottom: '5px'
    },
    input: {
      width: '50%',
      padding: '15px 20px',
      fontSize: 'clamp(0.9rem, 3vw, 1rem)',
      border: '2px solid #ddd',
      borderRadius: '8px',
      boxSizing: 'border-box',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
    },
    textarea: {
      width: '50%',
      padding: '15px 20px',
      fontSize: 'clamp(0.9rem, 3vw, 1rem)',
      border: '2px solid #ddd',
      borderRadius: '8px',
      boxSizing: 'border-box',
      minHeight: '80px',
      resize: 'vertical',
      backgroundColor: '#ffffff',
      transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
    },
    button: {
      padding: '12px 24px',
      fontSize: 'clamp(0.9rem, 3vw, 1rem)',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      margin: '5px',
      minWidth: '100px'
    }
  };
  const [manualEntry, setManualEntry] = useState({
    itemCode: '',
    itemName: '',
    quantity: '',
    unitPrice: '',
    gst: '',
    invoiceNumber: '',
    vendor: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData) {
      setManualEntry({
        itemCode: initialData.itemCode || '',
        itemName: initialData.itemName || '',
        quantity: initialData.quantity || '',
        unitPrice: initialData.unitPrice || '',
        gst: initialData.gst || '',
        invoiceNumber: initialData.invoiceNumber || '',
        vendor: initialData.vendor || '',
        purchaseDate: initialData.purchaseDate ? new Date(initialData.purchaseDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        notes: initialData.notes || ''
      });
    }
  }, [initialData]);

  const handleFileChange = (e) => {
    setBillFile(e.target.files[0]);
  };

  const handleManualChange = (e) => {
    const { name, value } = e.target;
    setManualEntry(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      if (billFile) {
        formData.append('bill', billFile);
      }
      Object.entries(manualEntry).forEach(([key, value]) => {
        formData.append(key, value);
      });

      let result;
      if (initialData) {
        result = await updatePurchase(initialData._id, formData, token);
      } else {
        result = await createPurchase(formData, token);
      }

      onSubmit(result.data);
      navigate('/purchasing-admin-panel/purchases/receipt-List');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save purchase receipt');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/purchasing-admin-panel');
  };
 return (
    <div className="purchase-receipt-form card-3d" style={{ 
      backgroundColor: '#997a8d', 
      padding: '20px',
      maxWidth: '100%',
      width: '100%',
      boxSizing: 'border-box'
    }}>
       <>
          {error && <div className="error-message" style={{ color: 'var(--rose-gold-accent)' }}>{error}</div>}

          <div className="form-section" style={responsiveStyles.formSection}>
            <h2 style={{ 
              fontSize: 'clamp(1.2rem, 4vw, 1.8rem)',
              margin: '0 0 10px 0',
              textAlign: 'center'
            }}>Add Purchase Receipt</h2>
            <label htmlFor="itemCode" style={responsiveStyles.label}>Item Code</label>
            <input
              type="text"
              name="itemCode"
              value={manualEntry.itemCode}
              onChange={handleManualChange}
              required
              style={responsiveStyles.input}
              onFocus={(e) => {
                e.target.style.borderColor = '#997a8d';
                e.target.style.boxShadow = '0 0 5px rgba(153, 122, 141, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ddd';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div className="form-section" style={responsiveStyles.formSection}>
            <label htmlFor="itemName" style={responsiveStyles.label}>Item Name</label>
            <input
              type="text"
              name="itemName"
              value={manualEntry.itemName}
              onChange={handleManualChange}
              required
              style={responsiveStyles.input}
            />
          </div>

          <div className="form-section" style={responsiveStyles.formSection}>
            <label htmlFor="quantity" style={responsiveStyles.label}>Quantity</label>
            <input
              type="number"
              name="quantity"
              value={manualEntry.quantity}
              onChange={handleManualChange}
              min="1"
              required
              style={responsiveStyles.input}
            />
          </div>

          <div className="form-section" style={responsiveStyles.formSection}>
            <label htmlFor="unitPrice" style={responsiveStyles.label}>Unit Price</label>
            <input
              type="number"
              name="unitPrice"
              value={manualEntry.unitPrice}
              onChange={handleManualChange}
              min="0.01"
              step="0.01"
              required
              style={responsiveStyles.input}
            />
          </div>

          <div className="form-section" style={responsiveStyles.formSection}>
            <label htmlFor="gst" style={responsiveStyles.label}>GST (%)</label>
            <input
              type="number"
              name="gst"
              value={manualEntry.gst}
              onChange={handleManualChange}
              min="0"
              step="0.1"
              required
              style={responsiveStyles.input}
            />
          </div>

          <div className="form-section" style={responsiveStyles.formSection}>
            <label htmlFor="invoiceNumber" style={responsiveStyles.label}>Invoice Number</label>
            <input
              type="text"
              name="invoiceNumber"
              value={manualEntry.invoiceNumber}
              onChange={handleManualChange}
              style={responsiveStyles.input}
            />
          </div>

          <div className="form-section" style={responsiveStyles.formSection}>
            <label htmlFor="vendor" style={responsiveStyles.label}>Vendor</label>
            <input
              type="text"
              name="vendor"
              value={manualEntry.vendor}
              onChange={handleManualChange}
              style={responsiveStyles.input}
            />
          </div>

          <div className="form-section" style={responsiveStyles.formSection}>
            <label htmlFor="purchaseDate" style={responsiveStyles.label}>Purchase Date</label>
            <input
              type="date"
              name="purchaseDate"
              value={manualEntry.purchaseDate}
              onChange={handleManualChange}
              required
              style={responsiveStyles.input}
            />
          </div>

          <div className="form-section" style={responsiveStyles.formSection}>
            <label htmlFor="notes" style={responsiveStyles.label}>Notes (Optional)</label>
            <textarea
              name="notes"
              value={manualEntry.notes}
              onChange={handleManualChange}
              style={responsiveStyles.textarea}
            />
          </div>

          <div className="form-section" style={responsiveStyles.formSection}>
            <label htmlFor="bill" style={responsiveStyles.label}>Upload Purchase Bill (PDF/Image)</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              style={responsiveStyles.input}
            />
          </div>

          <div className="form-actions" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            justifyContent: 'center',
            padding: '20px 0',
            marginTop: '20px'
          }}>
            <button
              className="btn-3d"
              onClick={handleSubmit}
              disabled={loading}
              style={{ 
                ...responsiveStyles.button,
                backgroundColor: 'var(--rose-gold-primary)',
                color: 'white'
              }}
            >
              {loading ? 'Submitting...' : (initialData ? 'Save' : 'Submit')}
            </button>
            {initialData && (
              <button
                className="btn-3d btn-edit"
                onClick={handleSubmit}
                disabled={loading}
                style={{ 
                  ...responsiveStyles.button,
                  backgroundColor: 'var(--rose-gold-accent)',
                  color: 'white'
                }}
              >
                Edit
              </button>
            )}
            <button
              className="btn-3d"
              onClick={handleBack}
              disabled={loading}
              style={{ 
                ...responsiveStyles.button,
                backgroundColor: '#b39eb5',
                color: 'white'
              }}
            >
              Back
            </button>
          </div>
        </>
    
    </div>
  );
};

export default PurchaseReceiptDetails;